/**
 * The pages that keep beating the learner, across every reading of a book.
 *
 * The report answers "how did I do", and `#/lib/flashbookReadHistory` answers
 * "better than last time". Both are about *how much*: a score, a climb. Neither can
 * answer the question a learner on their third read actually has, which is *where*
 * — the same page, the same exercise, three times running. A read is scored once;
 * the trouble it met is per page (see `#/lib/flashbookProgress`), so this file is
 * the addition: one page's worth of every read's trouble, summed.
 *
 * Four decisions shape it, and each is a way it could lie.
 *
 *  - **A page has to have cost something twice.** One bad page in one read is what
 *    that read's own report already lists, and repeating it here would make this
 *    section a second copy of "worth another look". Twice is a *pattern*, and a
 *    pattern is the only thing worth calling a page that keeps beating you — so
 *    `minReads` is 2 and the section says nothing at all until a page has lost
 *    something in two different readings.
 *  - **Only reads that were actually recorded page by page are counted.** Reads
 *    registered before the register kept this are silently older news, and the
 *    alternative — treating an absent digest as "nothing went wrong" — would make a
 *    page's count *fall* as an app is updated. Absent means unknown, so it is
 *    left out, and the section reports what it read rather than what it hoped.
 *  - **The page has to still be in the book.** Every row's entire action is turning
 *    back to it, and a page the creator has since deleted cannot be turned back to.
 *    The counts are of a book as it *is* — a book that lost a page keeps the
 *    readings it had on the ones it still has.
 *  - **A practice is not a reading.** Practices are registered beside the reads (see
 *    `#/lib/flashbookProgress`) and their questions are ones the read already
 *    counted; letting them add to a page's toll would report a learner who practised
 *    an eight-question page six times as having been beaten by it six times more
 *    than they were.
 *
 * Pure: a function of the register and the book, and nothing else, so every rule
 * above can be tested without a device or a screen.
 */
import {type FlashBookReadProgress} from '#/lib/flashbookProgress'
import {type FlashBookPage} from '#/types/flashbook'

/** How many pages the report names before it stops. */
export const TROUBLE_PAGES_SHOWN = 5

/** One page, and what it has cost across the readings. */
export type TroubledPage = {
  pageId: string
  /** Where the page is *now*, 0-based — what the row turns back to. */
  index: number
  /** The page's own title, or `''` when it has none. */
  title: string
  /** Readings in which it cost the learner at least one question. */
  reads: number
  /** Questions lost on it, over every reading. Never less than `reads`. */
  lost: number
  /**
   * When the last of those readings ended.
   *
   * Carried for the tie-break — two pages lost in two reads each are ranked by
   * which one was still costing questions most recently, which is the more useful
   * one to put in front of a learner — and because "and it happened again on your
   * last read" is the difference between a habit and a scar.
   */
  lastAt: string
}

export type FlashBookPageTrouble = {
  /** The pages that keep costing the learner, worst first. */
  pages: TroubledPage[]
  /**
   * Readings the counts are drawn from: ended reads that carry a page-by-page
   * record. Not the total number of reads — see the module note.
   */
  reads: number
}

/**
 * The pages that keep beating the learner in this book.
 *
 * @param progress The book's register, or `null` for a book with none.
 * @param pages The book as it is now — the only place a page's title and position
 *   are known, and the reason a deleted page disappears from the list.
 * @param minReads How many readings a page has to have cost something in. 2 by
 *   default (see the module note); 1 asks the looser question "which pages have
 *   cost me anything", which is what the unit tests use to look at the grouping
 *   without the pattern rule folded in.
 */
export function buildFlashBookPageTrouble({
  progress,
  pages,
  minReads = 2,
  limit = TROUBLE_PAGES_SHOWN,
}: {
  progress: FlashBookReadProgress | null | undefined
  pages: readonly FlashBookPage[]
  minReads?: number
  limit?: number
}): FlashBookPageTrouble {
  if (!progress) return {pages: [], reads: 0}

  /*
   * Every ended read that kept a page-by-page record, oldest first.
   *
   * Practices are dropped, and a read with no digest is dropped rather than read as
   * a clean one: `trouble` is absent on anything registered before the register
   * carried it, and only `Array.isArray` can tell that apart from a read that
   * trounced the book. A read that genuinely lost nothing keeps an empty array —
   * which *is* evidence, and is kept.
   */
  const recorded = progress.sessions
    .filter(
      session => session.kind !== 'practice' && Array.isArray(session.trouble),
    )
    .slice()
    .sort((a, b) => Date.parse(a.endedAt) - Date.parse(b.endedAt))

  if (recorded.length === 0) return {pages: [], reads: 0}

  /** Where each page lives in the book as it is now, by the page's own id. */
  const at = new Map(pages.map((page, index) => [page.id, index]))

  const tolls = new Map<string, TroubledPage>()
  for (const session of recorded) {
    for (const cost of session.trouble ?? []) {
      const index = at.get(cost.pageId)
      // Page one of the book is index 0, so the guard has to be `undefined` rather
      // than falsy — a page that is no longer in the book is skipped.
      if (index === undefined) continue
      const seen = tolls.get(cost.pageId)
      const page = pages[index]
      if (seen) {
        seen.reads += 1
        seen.lost += cost.lost
        // Oldest first, so the last write wins and this is the most recent read
        // that was troubled by the page.
        seen.lastAt = session.endedAt
      } else {
        tolls.set(cost.pageId, {
          pageId: cost.pageId,
          index,
          title: page.title?.trim() ?? '',
          reads: 1,
          lost: cost.lost,
          lastAt: session.endedAt,
        })
      }
    }
  }

  const ranked = [...tolls.values()]
    .filter(page => page.reads >= minReads)
    /*
     * Ranked by *how many readings*, not by how many questions: a page that cost one
     * question in four readings is a habit, while a page that cost six in one
     * reading is a bad afternoon. Ties go to the bigger toll, then to the page that
     * was still costing questions most recently, then to book order — so the list is
     * a fact rather than a shuffle, however the register is stored.
     */
    .sort(
      (a, b) =>
        b.reads - a.reads ||
        b.lost - a.lost ||
        Date.parse(b.lastAt) - Date.parse(a.lastAt) ||
        a.index - b.index,
    )
    .slice(0, Math.max(0, limit))

  return {pages: ranked, reads: recorded.length}
}
